import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { twMerge } from 'tailwind-merge'

const propTypes = {
  autoFocus: PropTypes.bool,
  autoResize: PropTypes.bool,
  className: PropTypes.string,
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  debounce: PropTypes.number,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  size: PropTypes.oneOf(['medium', 'large']).isRequired,
  style: PropTypes.object,
  type: PropTypes.string,
  value: PropTypes.string,
  hide: PropTypes.bool,
}

const TextArea = ({
  id,
  autoFocus,
  autoResize,
  className,
  defaultValue,
  value,
  type,
  size = 'large',
  placeholder,
  onChange = () => {},
  onKeyDown,
  onClick,
  onBlur,
  disabled,
  debounce,
  style,
  hide,
} = {}) => {
  const [inputValue, setInputValue] = useState(defaultValue ?? '')
  const textareaRef = useRef(null)
  const debounceTimeoutRef = useRef(null)

  const adjustHeight = () => {
    const textarea = textareaRef.current
    textarea.style.height = 'auto'
    textarea.style.height = `${textarea.scrollHeight + 3}px`
  }

  const onChangeListener = (e) => {
    if (value !== undefined) {
      onChange(e)
    } else {
      setInputValue(e.target.value)
      if (!isNaN(debounce)) {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current)
        }
        debounceTimeoutRef.current = setTimeout(() => {
          onChange(e)
        }, debounce)
      } else {
        onChange(e)
      }
    }

    if (autoResize) {
      adjustHeight()
    }
  }

  useEffect(() => {
    if (autoResize) {
      adjustHeight()
    }
  }, [])

  const sizeClasses = {
    large: 'h-9 px-4 py-2',
    medium: 'h-8 px-3 py-2',
  }

  return (
    <textarea
      ref={textareaRef}
      id={id}
      autoFocus={autoFocus}
      placeholder={placeholder}
      value={value !== undefined ? value : inputValue}
      onChange={onChangeListener}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      type={type}
      style={style}
      className={twMerge(
        'scrollable bg-foreground/5 text-foreground flex min-h-[50px] flex-shrink-0 items-center gap-2 rounded-lg border border-transparent text-sm font-normal leading-5 shadow-sm transition-colors',
        sizeClasses[size],
        'hover:border-border/10 hover:bg-foreground/8',
        'focus:border-brand focus:outline-none',
        disabled && 'pointer-events-none opacity-35',
        hide && 'hidden',
        className,
      )}
    />
  )
}

TextArea.propTypes = propTypes

export default TextArea
